import React, { useContext, useEffect, useState } from 'react';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { useLazyQuery } from '@apollo/client';
import { Button, Tooltip } from '@mui/material';
import { CalculatorsContext, DraftContext } from '../../context';
import styles from './CalculationCost.module.scss';
import { GET_PRICE } from '../../api/apollo/api';
import { getQuickCost } from '../../api/rest/endpoints/draft/requests';
import clsx from 'clsx';
import useDebounce from '../../utils/useDebounce';

const tooltipText = `
  Stock exchanges undertake a range of activities and fulfil a number of functions. When defining stock exchanges, their role is typically captured in terms of their listing services and trading function (or liquidity provision). An important related function is that of price formation.
  While vitally important, price formation is often not mentioned explicitly—partly perhaps because it is much less tangible, and partly because it is (sometimes) assumed to be part of the trading function. Indeed, trading and price formation are closely related—but more on this later.
  So what do we mean by price formation?
  In our report we turned to the well-established economics literature on market microstructure to understand the price formation process and its benefits to the economy.
  Price formation is an information-gathering process which ensures that market participants know enough about the prices of the assets being traded in the market, so that they can make well-informed decisions.
  `;

const CalculationCost = ({ isItemsComponents }) => {
  const { calculatorPrices, calculatorName, carbonAnswers, localAnswers, calculatorsList } =
    useContext(CalculatorsContext);

  const { draftId } = useContext(DraftContext);

  const [open, setOpen] = useState(false);
  const [cost, setCost] = useState(null);

  const [carbonFilledAnswersCount, setCarbonFilledAnswersCount] = useState(0);
  const [calcs, setCalcs] = useState([]);

  const { tariffCode, tariffName, attemptsLeft } = calculatorPrices;

  const debounce = useDebounce((payload) => {
    getQuickCost(payload).then((res) => {
      const { data, success } = res.data;
      if (success) setCost(data);
    });
  }, 1000);

  useEffect(() => {
    if (!draftId) return;
    debounce({
      draft_id: draftId,
      calculators: calcs,
      is_discount: attemptsLeft ? '1' : '0',
      carbon_count: `${carbonFilledAnswersCount}`,
    });
  }, [carbonFilledAnswersCount, draftId]);

  useEffect(() => {
    const newCalcs = [];

    const newCarbonFilledAnswersCount = Object.keys(carbonAnswers ?? {}).reduce(
      (res, category) =>
        res +
        Object.keys(carbonAnswers[category]).reduce(
          (res2, question) =>
            res2 + carbonAnswers[category][question].filter((activ) => activ?.filled).length,
          0
        ),
      0
    );

    if (newCarbonFilledAnswersCount > 0) newCalcs.push('carbon');

    setCarbonFilledAnswersCount(newCarbonFilledAnswersCount);
    setCalcs(newCalcs);
    // save data to the draft with filtering empty fields
  }, [carbonAnswers, calculatorsList]);

  const [size, setSize] = useState();

  useEffect(() => {
    const handleResize = () => {
      setSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const getTextBlock = () => {
    if (!cost || !calculatorName || !calculatorPrices) return <></>;

    const tariffString =
      size > 1023 ? (
        <>
          {' Your current plan:'}
          <span>&nbsp;{tariffName}.</span>
        </>
      ) : (
        ''
      );
    const chargeString =
      tariffCode !== 'free' ? (
        <>
          {' You have'}
          <span>&nbsp;{attemptsLeft}&nbsp;</span>
          {' of charge calculation.'}
        </>
      ) : (
        ''
      );
    const worthString =
      tariffCode === 'standard' && cost.baseCost !== 0 ? (
        <>
          {' That worth'}
          <span>&nbsp;${cost.baseCost}.</span>
        </>
      ) : (
        ''
      );
    const costString =
      cost.finalCost > 0 ? (
        <>
          {' The calculation cost:'}
          <span>&nbsp;${cost.finalCost}.</span>
        </>
      ) : (
        ''
      );
    return (
      <>
        {!!tariffString && tariffString}
        {!!chargeString && chargeString}
        {!!worthString && worthString}
        {!!costString && costString}
      </>
    );
  };

  return (
    <>
      {cost && (
        <div
          className={clsx(styles.calculatorCostOuterBlock, {
            [styles.fit]: isItemsComponents,
          })}
        >
          <div className={styles.calculatorCostInnerBlock}>
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <>
                <div className={styles.calculatorCostText}>
                  <div className={styles.tariffText}>
                    <div className={styles.calculatorCostText}>
                      {getTextBlock()}
                      <div className={styles.calculatorCostIco}>
                        <Tooltip
                          PopperProps={{
                            disablePortal: true,
                          }}
                          arrow
                          placement="top-end"
                          onClose={() => setOpen(false)}
                          open={open}
                          // disableFocusListener
                          disableHoverListener
                          // disableTouchListener
                          title={tooltipText}
                        >
                          <Button
                            className={styles.buttonHelp}
                            onClick={() => setOpen((value) => !value)}
                          >
                            <HelpOutlineRoundedIcon className={styles.helpIcon} />
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            </ClickAwayListener>
          </div>
        </div>
      )}
    </>
  );
};

export default CalculationCost;
